import React from 'react';
import { Link } from 'react-router-dom';

interface ContractorCardProps {
  contractorId: number;
  name: string;
  rating: number;
  experience: string;
  category: string;
  price: string;
  image: string;
  status?: 'PREMIUM' | 'Popular';
  reviews?: number;
  isVerified?: boolean;
  priceUnit?: string;
  isMobile?: boolean;
  isDesktop?: boolean;
}

const ContractorCard: React.FC<ContractorCardProps> = ({
  contractorId,
  name,
  rating,
  experience,
  category,
  price,
  image,
  status,
  reviews,
  isVerified,
  priceUnit,
  isMobile,
  isDesktop,
}) => {
  const renderStars = (currentRating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (currentRating >= i) {
        stars.push(<span key={i} className="material-symbols-outlined text-sm" data-icon="star" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>);
      } else if (currentRating > i - 1) {
        stars.push(<span key={i} className="material-symbols-outlined text-sm" data-icon="star_half" style={{ fontVariationSettings: "'FILL' 1" }}>star_half</span>);
      } else {
        stars.push(<span key={i} className="material-symbols-outlined text-sm" data-icon="star">star</span>);
      }
    }
    return stars;
  };

  if (isMobile) {
    return (
      <div className="bg-surface-container-lowest rounded-xl p-4 shadow-sm border border-outline-variant flex gap-4">
        <div className="relative w-24 h-24 flex-shrink-0">
          <img className="w-full h-full object-cover rounded-lg" alt={name} src={image} />
          <div className="absolute -top-1 -right-1 bg-white rounded-full p-0.5">
            <span className="material-symbols-outlined text-[#22c55e] text-[20px]" data-icon="verified" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
          </div>
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex justify-between items-start">
            <h3 className="font-headline-sm text-on-surface">{name}</h3>
            <div className="flex items-center gap-1">
              <span className="material-symbols-outlined text-[#fbbf24] text-[18px]" data-icon="star" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
              <span className="font-label-bold text-on-surface">{rating}</span>
            </div>
          </div>
          <p className="font-body-md text-on-surface-variant">{category} • {experience}</p>
          {status && (
            <div className="pt-2">
              <span className={`px-2 py-0.5 rounded text-[10px] font-label-bold ${status === 'PREMIUM' ? 'bg-surface-container text-on-surface-variant' : 'bg-[#e0f2fe] text-[#0369a1]'}`}>
                {status}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isDesktop) {
    return (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-outline-variant/20 hover:shadow-lg transition-all group">
        <div className="relative h-48">
          <img alt="Professional contractor" className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-500" src={image} />
          {isVerified && (
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-2 py-1 rounded flex items-center gap-1">
              <span className="material-symbols-outlined text-error text-[16px]" data-icon="verified" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
              <span className="font-label-bold text-[10px] text-primary uppercase">Verified</span>
            </div>
          )}
        </div>
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="font-headline-sm text-headline-sm text-primary">{name}</h3>
              <div className="flex items-center gap-1 text-[#FFB800]">
                {renderStars(rating)}
                {reviews && <span className="text-on-surface-variant text-body-md ml-1">({reviews} reviews)</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-surface-container px-3 py-1 rounded-full text-label-bold text-on-surface-variant">{experience}</div>
            <div className="bg-secondary-container/10 px-3 py-1 rounded-full text-label-bold text-secondary">{category}</div>
          </div>
          <div className="flex items-center justify-end pt-4 border-t border-outline-variant/30">
            <Link to={`/contractors/${contractorId}`} className="bg-primary text-on-primary px-4 py-2 rounded-lg font-label-bold text-label-bold hover:bg-primary-container transition-colors">View Profile</Link>
          </div>
        </div>
      </div>
    );
  }

  return null; // Should not happen if isMobile or isDesktop is true
};

export default ContractorCard;
